import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
// @ts-ignore - TS 4.9.5 can't resolve all exports from lucide-react's large barrel d.ts
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';
// @ts-ignore
import PackageCheck from 'lucide-react/dist/esm/icons/package-check';
// @ts-ignore
import Truck from 'lucide-react/dist/esm/icons/truck';
// @ts-ignore
import Clock from 'lucide-react/dist/esm/icons/clock';
// @ts-ignore
import CheckCircle from 'lucide-react/dist/esm/icons/check-circle';
// @ts-ignore
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
// @ts-ignore
import XCircle from 'lucide-react/dist/esm/icons/x-circle';
// @ts-ignore
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
// @ts-ignore
import CloudOff from 'lucide-react/dist/esm/icons/cloud-off';
// @ts-ignore
import WifiOff from 'lucide-react/dist/esm/icons/wifi-off';
// @ts-ignore
import Package from 'lucide-react/dist/esm/icons/package';
import { listarConferenciasConferente, listarPaletesHistoricoConferente } from '../../services/conferente';
import type { PaleteHistorico } from '../../services/conferente';
import type { Conferencia } from '../../types/conferencia';
import { useOnlineStatus } from '../../hooks/useOnlineStatus';
import { listarConferenciasOffline } from '../../services/offlineData';
import { listarConferenciasPendentes } from '../../services/offlineQueue';

const TIPO_LABELS: Record<string, { label: string; color: string }> = {
  pre_carregamento: { label: 'Pré-Carregamento', color: 'text-amber-600' },
  carregamento: { label: 'Carregamento', color: 'text-blue-600' },
  descarregamento: { label: 'Descarregamento', color: 'text-green-600' }
};

const STATUS_ICON: Record<string, React.ReactElement> = {
  pendente: <Clock className="w-6 h-6 text-gray-400" />,
  em_andamento: <Clock className="w-6 h-6 text-blue-500" />,
  parcial: <AlertTriangle className="w-6 h-6 text-yellow-500" />,
  concluida: <CheckCircle className="w-6 h-6 text-green-500" />,
  divergente: <XCircle className="w-6 h-6 text-red-500" />,
  cancelada: <XCircle className="w-6 h-6 text-gray-400" />
};

const STATUS_LABELS: Record<string, string> = {
  pendente: 'Pendente',
  em_andamento: 'Em andamento',
  parcial: 'Parcial',
  concluida: 'Concluída',
  divergente: 'Divergente',
  cancelada: 'Cancelada'
};

type VisaoTipo = 'conferencias' | 'paletes';

const ConferenteHistorico: React.FC = () => {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const [visao, setVisao] = useState<VisaoTipo>('paletes');
  const [conferencias, setConferencias] = useState<Conferencia[]>([]);
  const [conferenciasOfflinePendentes, setConferenciasOfflinePendentes] = useState<any[]>([]);
  const [paletes, setPaletes] = useState<PaleteHistorico[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filtroTipo, setFiltroTipo] = useState('');
  const [erro, setErro] = useState('');
  const [usandoCache, setUsandoCache] = useState(false);

  const carregarConferencias = useCallback(async () => {
    try {
      setLoading(true);
      setErro('');
      setUsandoCache(false);
      const params: Record<string, string | number> = { page, limit: 15 };
      if (filtroTipo) params.tipo = filtroTipo;
      const result = await listarConferenciasConferente(params);
      setConferencias(result.conferencias);
      setTotalPages(result.pagination.totalPages);
    } catch (error: any) {
      console.error('Erro ao carregar histórico:', error);
      // Fallback: usar dados do cache offline
      try {
        const [cached, pendentes] = await Promise.all([
          listarConferenciasOffline(),
          listarConferenciasPendentes(),
        ]);
        let todasOffline = cached.map((c: any) => ({
          ...c,
          criado_em: c.criado_em || c.created_at || new Date().toISOString(),
        }));
        if (filtroTipo) {
          todasOffline = todasOffline.filter((c: any) => c.tipo === filtroTipo);
        }
        if (todasOffline.length > 0 || pendentes.length > 0) {
          setConferencias(todasOffline);
          setConferenciasOfflinePendentes(
            pendentes.filter(c => c.status !== 'sincronizado')
              .filter(c => !filtroTipo || c.tipo === filtroTipo)
          );
          setTotalPages(1);
          setUsandoCache(true);
        } else {
          setErro(error.message || 'Erro ao carregar histórico. Verifique sua conexão.');
        }
      } catch {
        setErro(error.message || 'Erro ao carregar histórico. Verifique sua conexão.');
      }
    } finally {
      setLoading(false);
    }
  }, [page, filtroTipo]);

  const carregarPaletes = useCallback(async () => {
    try {
      setLoading(true);
      setErro('');
      const result = await listarPaletesHistoricoConferente();
      setPaletes(result.paletes);
    } catch (error: any) {
      console.error('Erro ao carregar paletes:', error);
      setErro(error.message || 'Erro ao carregar paletes. Verifique sua conexão.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (visao === 'conferencias') {
      carregarConferencias();
    } else {
      carregarPaletes();
    }
  }, [visao, carregarConferencias, carregarPaletes]);

  const handleRecarregar = () => {
    if (visao === 'conferencias') {
      carregarConferencias();
    } else {
      carregarPaletes();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Offline Banner */}
      {!isOnline && (
        <div className="bg-yellow-500 text-white px-4 py-2 flex items-center justify-center gap-2 text-sm font-medium safe-top">
          <WifiOff className="w-4 h-4" />
          Sem conexão - dados podem estar desatualizados
        </div>
      )}

      {/* Header */}
      <div className={`bg-green-600 text-white px-4 py-4 ${isOnline ? 'safe-top' : ''}`}>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/conferente')} className="p-2 hover:bg-white/10 rounded-lg transition" aria-label="Voltar ao dashboard">
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div>
            <h1 className="text-lg font-bold">Minhas Conferências</h1>
            <p className="text-sm text-green-200">Histórico completo</p>
          </div>
        </div>

        {/* Toggle Conferências / Paletes */}
        <div className="mt-3 flex bg-white/20 rounded-xl p-1">
          <button
            onClick={() => setVisao('paletes')}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
              visao === 'paletes'
                ? 'bg-white text-green-700 shadow-sm'
                : 'text-green-100 hover:bg-white/10'
            }`}
          >
            Paletes
          </button>
          <button
            onClick={() => { setVisao('conferencias'); setPage(1); }}
            className={`flex-1 py-2 rounded-lg text-sm font-medium transition ${
              visao === 'conferencias'
                ? 'bg-white text-green-700 shadow-sm'
                : 'text-green-100 hover:bg-white/10'
            }`}
          >
            Conferências
          </button>
        </div>

        {/* Filtro de tipo (apenas para conferências) */}
        {visao === 'conferencias' && (
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            {['', 'pre_carregamento', 'carregamento', 'descarregamento'].map(tipo => (
              <button
                key={tipo}
                onClick={() => { setFiltroTipo(tipo); setPage(1); }}
                className={`px-4 py-2 rounded-full text-sm font-medium transition whitespace-nowrap ${
                  filtroTipo === tipo
                    ? 'bg-white text-green-700'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {tipo === '' ? 'Todos' : TIPO_LABELS[tipo]?.label || tipo}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lista */}
      <div className="flex-1 overflow-y-auto">
        {usandoCache && visao === 'conferencias' && (
          <div className="mx-4 mt-4 bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-center gap-2">
            <CloudOff className="w-5 h-5 text-blue-600 flex-shrink-0" />
            <p className="text-sm text-blue-700">Exibindo dados do cache offline. Conecte a internet para ver o histórico completo.</p>
          </div>
        )}

        {/* Conferencias criadas offline */}
        {visao === 'conferencias' && conferenciasOfflinePendentes.length > 0 && (
          <div className="mx-4 mt-4">
            <h3 className="text-sm font-semibold text-blue-700 mb-2 flex items-center gap-1">
              <CloudOff className="w-4 h-4" />
              Conferências Offline ({conferenciasOfflinePendentes.length})
            </h3>
            <div className="divide-y divide-blue-100 bg-blue-50 rounded-xl overflow-hidden border border-blue-200">
              {conferenciasOfflinePendentes.map(conf => {
                const tipoInfo = TIPO_LABELS[conf.tipo] || TIPO_LABELS.carregamento;
                const isFalha = conf.status === 'falha_permanente';
                return (
                  <button
                    key={conf.temp_id}
                    onClick={() => navigate(`/conferente/conferencia/${conf.temp_id}`)}
                    className="w-full px-4 py-4 flex items-center gap-3 hover:bg-blue-100/50 transition text-left active:bg-blue-100"
                  >
                    <CloudOff className={`w-5 h-5 flex-shrink-0 ${isFalha ? 'text-red-500' : 'text-blue-500'}`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-base font-semibold ${tipoInfo.color}`}>
                          Offline
                        </span>
                        <span className="text-sm text-gray-500">{tipoInfo.label}</span>
                        {isFalha && (
                          <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Erro</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        {conf.placa_veiculo && (
                          <span className="text-sm text-gray-400 flex items-center gap-1">
                            <Truck className="w-4 h-4" />
                            {conf.placa_veiculo}
                          </span>
                        )}
                        <span className="text-sm text-gray-400">
                          {new Date(conf.criado_em).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {erro && (
          <div className="mx-4 mt-4 bg-red-50 border border-red-200 rounded-xl p-4 flex items-center justify-between gap-3">
            <p className="text-sm text-red-700">{erro}</p>
            <button
              onClick={handleRecarregar}
              className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition flex-shrink-0"
            >
              Tentar novamente
            </button>
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-3 border-green-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : visao === 'conferencias' ? (
          /* === VISÃO CONFERÊNCIAS === */
          conferencias.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <PackageCheck className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-base">Nenhuma conferência encontrada</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {conferencias.map(conf => {
                const tipoInfo = TIPO_LABELS[conf.tipo] || TIPO_LABELS.carregamento;
                const progresso = conf.total_itens_esperado > 0
                  ? Math.round((conf.total_itens_conferido / conf.total_itens_esperado) * 100)
                  : 0;

                return (
                  <button
                    key={conf.id}
                    onClick={() => navigate(`/conferente/conferencia/${conf.id}`)}
                    className="w-full bg-white px-4 py-4 flex items-center gap-3 hover:bg-gray-50 transition text-left active:bg-gray-100"
                  >
                    <div className="flex-shrink-0">
                      {STATUS_ICON[conf.status] || STATUS_ICON.pendente}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-base font-semibold ${tipoInfo.color}`}>
                          #{conf.id}
                        </span>
                        <span className="text-sm text-gray-500">{tipoInfo.label}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          conf.status === 'concluida' ? 'bg-green-100 text-green-700' :
                          conf.status === 'em_andamento' ? 'bg-blue-100 text-blue-700' :
                          conf.status === 'divergente' ? 'bg-red-100 text-red-700' :
                          conf.status === 'parcial' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {STATUS_LABELS[conf.status]}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 mt-1">
                        {conf.placa_veiculo && (
                          <span className="text-sm text-gray-400 flex items-center gap-1">
                            <Truck className="w-4 h-4" />
                            {conf.placa_veiculo}
                          </span>
                        )}
                        <span className="text-sm text-gray-400">
                          {new Date(conf.criado_em).toLocaleDateString('pt-BR')}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="flex-1 bg-gray-100 rounded-full h-2">
                          <div
                            className={`h-2 rounded-full ${progresso >= 100 ? 'bg-green-500' : 'bg-blue-500'}`}
                            style={{ width: `${Math.min(progresso, 100)}%` }}
                          />
                        </div>
                        <span className="text-xs text-gray-500 flex-shrink-0 font-medium">
                          {conf.total_itens_conferido}/{conf.total_itens_esperado}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
                  </button>
                );
              })}
            </div>
          )
        ) : (
          /* === VISÃO PALETES === */
          paletes.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-base">Nenhum palete encontrado</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {paletes.map(palete => {
                const statusPalete = palete.status;
                const podeRetomar = statusPalete === 'em_andamento';

                const handleClickPalete = () => {
                  if (podeRetomar) {
                    navigate('/conferente/pre_carregamento', { state: { numeroPalete: palete.numero_palete } });
                  }
                };

                return (
                  <div
                    key={palete.id}
                    onClick={handleClickPalete}
                    role={podeRetomar ? 'button' : undefined}
                    tabIndex={podeRetomar ? 0 : undefined}
                    className={`w-full bg-white px-4 py-4 flex items-center gap-3 text-left ${
                      podeRetomar
                        ? 'hover:bg-gray-50 cursor-pointer active:bg-gray-100'
                        : ''
                    }`}
                  >
                    <div className="flex-shrink-0">
                      {statusPalete === 'concluida'
                        ? <CheckCircle className="w-6 h-6 text-green-500" />
                        : statusPalete === 'em_andamento'
                        ? <Clock className="w-6 h-6 text-blue-500" />
                        : <Clock className="w-6 h-6 text-gray-400" />
                      }
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-base font-semibold text-amber-700">
                          Palete #{palete.numero_palete}
                        </span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          statusPalete === 'concluida' ? 'bg-green-100 text-green-700' :
                          statusPalete === 'em_andamento' ? 'bg-blue-100 text-blue-700' :
                          'bg-gray-100 text-gray-600'
                        }`}>
                          {STATUS_LABELS[statusPalete] || statusPalete}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-sm text-gray-500">
                          {palete.area_nome}
                          {palete.area_regiao && ` (${palete.area_regiao})`}
                        </span>
                        {palete.doca && (
                          <span className="text-sm text-gray-400">| Doca: {palete.doca}</span>
                        )}
                      </div>
                      {palete.cliente_razao_social && (
                        <p className="text-xs text-gray-400 mt-0.5 truncate">{palete.cliente_razao_social}</p>
                      )}
                      {palete.total_volumes > 0 && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs text-gray-500 font-medium">
                            {palete.total_volumes} etiqueta{palete.total_volumes !== 1 ? 's' : ''} bipada{palete.total_volumes !== 1 ? 's' : ''}
                          </span>
                          {palete.total_duplicados > 0 && (
                            <span className="text-xs text-orange-500">
                              ({palete.total_duplicados} duplicada{palete.total_duplicados !== 1 ? 's' : ''})
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {podeRetomar && (
                      <ChevronRight className="w-5 h-5 text-gray-300 flex-shrink-0" />
                    )}
                  </div>
                );
              })}
            </div>
          )
        )}
      </div>

      {/* Paginação (apenas para conferências) */}
      {visao === 'conferencias' && totalPages > 1 && (
        <div className="bg-white border-t border-gray-200 px-4 py-3 flex items-center justify-between safe-bottom">
          <button
            disabled={page <= 1}
            onClick={() => setPage(p => p - 1)}
            className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium disabled:opacity-50 hover:bg-gray-50 transition active:bg-gray-100"
          >
            Anterior
          </button>
          <span className="text-sm text-gray-600 font-medium">{page}/{totalPages}</span>
          <button
            disabled={page >= totalPages}
            onClick={() => setPage(p => p + 1)}
            className="px-4 py-2.5 border border-gray-300 rounded-xl text-sm font-medium disabled:opacity-50 hover:bg-gray-50 transition active:bg-gray-100"
          >
            Próxima
          </button>
        </div>
      )}
    </div>
  );
};

export default ConferenteHistorico;
